import { lazy, Suspense, useMemo } from "react";
import { createBrowserRouter, Navigate, useLocation, useSearchParams } from "react-router-dom";
import {
  AuthRouter,
  AUTH_ROUTES,
  resolveOAuthCallback,
  type AuthPageRoute,
} from "@ticketon/authentication-core";
import { AuthGuard } from "@/auth/AuthGuard";
import { AdminLayout } from "@/components/layout";
import { RouteErrorBoundary } from "@/components/shared";
import { PrivateRoute } from "@/router";

// Unauthorized page
const UnauthorizedPage = lazy(() => import("@/pages/UnauthorizedPage"));
const OAuthCallbackPage = lazy(() => import("@/auth/OAuthCallbackPage"));

// Module Routes and Permissions
import { DashboardRoutes, DashboardPermissions } from "@/modules/dashboard";
import { ReservationRoutes, ReservationPermissions } from "@/modules/reservations";
import { FlightRoutes, FlightPermissions } from "@/modules/flights";
import { HotelRoutes, HotelPermissions } from "@/modules/hotels";
import { PackageRoutes, PackagePermissions } from "@/modules/packages";
import { TransferRoutes, TransferPermissions } from "@/modules/transfers";
import { CustomerRoutes, CustomerPermissions } from "@/modules/customers";
import { SupportRoutes, SupportPermissions } from "@/modules/support";
import { ReportRoutes, ReportPermissions } from "@/modules/reporting";
import { SettingsRoutes, SettingsPermissions } from "@/modules/settings";
import { LandingPageRoutes, LandingPagePermissions, SharedComponentRoutes } from "@/modules/landing-pages";
import { AssetRoutes, ASSET_BROWSE_PERMISSIONS } from "@/modules/assets";
import { TravelAnalyticsRoutes, TravelAnalyticsPermissions } from "@/modules/travel-analytics";
import { AdvancedAnalyticsRoutes, AdvancedAnalyticsPermissions } from "@/modules/advanced-analytics";
import { DealsRoutes, DealsPermissions } from "@/modules/deals";
import { ProviderRoutes, ProviderPermissions } from "@/modules/providers";
import { IssueSupportTemplateRoutes, IssueSupportTemplatePermissions } from "@/modules/issue-support-templates";
import { EmailTemplateRoutes, EmailTemplatePermissions, AirlinePermissions } from "@/modules/email-templates";
import { EmailAutomationRoutes, EmailAutomationPermissions } from "@/modules/email-automations";
import { TravelUpsellRoutes, TravelUpsellPermissions } from "@/modules/upsells";

// Lazy loaded pages
const DashboardPage = lazy(() => import("@/modules/dashboard/pages/DashboardPage"));
const ReservationListPage = lazy(() => import("@/modules/reservations/pages/ReservationListPage"));
const ReservationDetailPage = lazy(() => import("@/modules/reservations/pages/ReservationDetailPage"));
const FlightListPage = lazy(() => import("@/modules/flights/pages/FlightListPage"));
const HotelListPage = lazy(() => import("@/modules/hotels/pages/HotelListPage"));
const PackageListPage = lazy(() => import("@/modules/packages/pages/PackageListPage"));
const TransferListPage = lazy(() => import("@/modules/transfers/pages/TransferListPage"));
const CustomerListPage = lazy(() => import("@/modules/customers/pages/CustomerListPage"));
const CustomerDetailPage = lazy(() => import("@/modules/customers/pages/CustomerDetailPage"));
const SupportTicketListPage = lazy(() => import("@/modules/support/pages/SupportTicketListPage"));
const SupportTicketDetailPage = lazy(() => import("@/modules/support/pages/SupportTicketDetailPage"));
const ReportListPage = lazy(() => import("@/modules/reporting/pages/ReportListPage"));
const SettingsPage = lazy(() => import("@/modules/settings/pages/SettingsPage"));
const ProviderListPage = lazy(() => import("@/modules/providers/pages/ProviderListPage"));
const ProviderDetailPage = lazy(() => import("@/modules/providers/pages/ProviderDetailPage"));
const ProviderFormPage = lazy(() => import("@/modules/providers/pages/ProviderFormPage"));
const IssueSupportTemplateListPage = lazy(() => import("@/modules/issue-support-templates/pages/IssueSupportTemplateListPage"));
const IssueSupportTemplateFormPage = lazy(() => import("@/modules/issue-support-templates/pages/IssueSupportTemplateFormPage"));
const IssueSupportTemplateDetailPage = lazy(() => import("@/modules/issue-support-templates/pages/IssueSupportTemplateDetailPage"));
const AssetLibraryPage = lazy(() => import("@/modules/assets/pages/AssetLibraryPage"));
const EmailTemplatesListPage = lazy(() => import("@/modules/email-templates/pages/EmailTemplatesListPage"));
const EmailTemplateEditorPage = lazy(() => import("@/modules/email-templates/pages/EmailTemplateEditorPage"));
const AirlinesPage = lazy(() => import("@/modules/email-templates/pages/AirlinesPage"));
const EmailBrandPage = lazy(() => import("@/modules/email-templates/pages/EmailBrandPage"));
const EmailAutomationsListPage = lazy(() => import("@/modules/email-automations/pages/EmailAutomationsListPage"));
const EmailAutomationEditorPage = lazy(() => import("@/modules/email-automations/pages/EmailAutomationEditorPage"));
const EmailAutomationSendLogPage = lazy(() => import("@/modules/email-automations/pages/EmailAutomationSendLogPage"));
const TravelUpsellsListPage = lazy(() => import("@/modules/upsells/pages/TravelUpsellsListPage"));
const TravelUpsellEditorPage = lazy(() => import("@/modules/upsells/pages/TravelUpsellEditorPage"));
const TravelUpsellDestinationsPage = lazy(() => import("@/modules/upsells/pages/TravelUpsellDestinationsPage"));
const UserPermissionsPage = lazy(() => import("@/modules/settings/pages/UserPermissionsPage"));
const LandingPagesDashboardPage = lazy(() => import("@/modules/landing-pages/pages/LandingPagesDashboardPage"));
const LandingPageListPage = lazy(() => import("@/modules/landing-pages/pages/LandingPageListPage"));
const LandingPageDetailPage = lazy(() => import("@/modules/landing-pages/pages/LandingPageDetailPage"));
const LandingPageEditorPage = lazy(() => import("@/modules/landing-pages/pages/LandingPageEditorPage"));
const SharedComponentListPage = lazy(() => import("@/modules/landing-pages/pages/SharedComponentListPage"));
const SharedComponentFormPage = lazy(() => import("@/modules/landing-pages/pages/SharedComponentFormPage"));

// Deals pages
const TravelDealsListPage = lazy(() => import("@/modules/deals/pages/TravelDealsListPage"));
const ComboOffersListPage = lazy(() => import("@/modules/deals/pages/ComboOffersListPage"));
const TravelDealsFormPage = lazy(() => import("@/modules/deals/pages/TravelDealsFormPage"));
const ComboOffersFormPage = lazy(() => import("@/modules/deals/pages/ComboOffersFormPage"));

// Travel Analytics pages
const AnalyticsOverviewPage = lazy(() => import("@/modules/travel-analytics/pages/AnalyticsOverviewPage"));
const FlightAnalyticsPage = lazy(() => import("@/modules/travel-analytics/pages/FlightAnalyticsPage"));
const HotelAnalyticsPage = lazy(() => import("@/modules/travel-analytics/pages/HotelAnalyticsPage"));
const CarAnalyticsPage = lazy(() => import("@/modules/travel-analytics/pages/CarAnalyticsPage"));
const PerformanceAnalyticsPage = lazy(() => import("@/modules/travel-analytics/pages/PerformanceAnalyticsPage"));
const SearchQualityPage = lazy(() => import("@/modules/travel-analytics/pages/SearchQualityPage"));
const ConversionFunnelPage = lazy(() => import("@/modules/travel-analytics/pages/ConversionFunnelPage"));
const ProviderAnalyticsPage = lazy(() => import("@/modules/travel-analytics/pages/ProviderAnalyticsPage"));
const GeographicAnalyticsPage = lazy(() => import("@/modules/travel-analytics/pages/GeographicAnalyticsPage"));
const SearchExplorerPage = lazy(() => import("@/modules/travel-analytics/pages/SearchExplorerPage"));

// Advanced Analytics pages
const FunnelsPage = lazy(() => import("@/modules/advanced-analytics/pages/FunnelsPage"));
const FlowsPage = lazy(() => import("@/modules/advanced-analytics/pages/FlowsPage"));
const RetentionPage = lazy(() => import("@/modules/advanced-analytics/pages/RetentionPage"));
const CohortsPage = lazy(() => import("@/modules/advanced-analytics/pages/CohortsPage"));
const CohortDetailPage = lazy(() => import("@/modules/advanced-analytics/pages/CohortDetailPage"));
const AdvUsersPage = lazy(() => import("@/modules/advanced-analytics/pages/UsersPage"));
const UserProfilePage = lazy(() => import("@/modules/advanced-analytics/pages/UserProfilePage"));
const BoardsListPage = lazy(() => import("@/modules/advanced-analytics/pages/BoardsListPage"));
const BoardDetailPage = lazy(() => import("@/modules/advanced-analytics/pages/BoardDetailPage"));
const SavedReportsPage = lazy(() => import("@/modules/advanced-analytics/pages/SavedReportsPage"));
const AlertsPage = lazy(() => import("@/modules/advanced-analytics/pages/AlertsPage"));
const EventExplorerPage = lazy(() => import("@/modules/advanced-analytics/pages/EventExplorerPage"));
const DataDictionaryPage = lazy(() => import("@/modules/advanced-analytics/pages/DataDictionaryPage"));
const GovernancePage = lazy(() => import("@/modules/advanced-analytics/pages/GovernancePage"));

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ticketon-nav" />
    </div>
  );
}

// Wrap lazy component with Suspense
function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function AuthRoutePage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const pathToRoute: Record<string, AuthPageRoute> = {
    [AUTH_ROUTES.LOGIN]: "login",
    [AUTH_ROUTES.FORGOT_PASSWORD]: "forgot-password",
    [AUTH_ROUTES.RESET_PASSWORD]: "validate-forgot-password",
  };

  const route = pathToRoute[location.pathname] || "login";
  const email = searchParams.get("email") || undefined;
  const code = searchParams.get("code") || undefined;
  const errorParam = searchParams.get("error");

  // Shared resolver turns the OAuth callback query into the route + linking data. The admin
  // disables self sign-up, so socialAuthData (pending_registration) is intentionally ignored;
  // account linking still works because it targets an existing account, not a new registration.
  const { effectiveRoute, accountLinkingData } = useMemo(
    () =>
      resolveOAuthCallback({
        urlRoute: route,
        status: searchParams.get("status"),
        accessToken: searchParams.get("accessToken"),
        email,
        provider: searchParams.get("provider"),
        firstName: searchParams.get("firstName"),
        lastName: searchParams.get("lastName"),
      }),
    [route, email, searchParams]
  );

  return (
    <AuthRouter
      route={effectiveRoute}
      urlEmail={email}
      code={code}
      showSignUpLink={false}
      initialError={errorParam ? decodeURIComponent(errorParam) : undefined}
      accountLinkingData={accountLinkingData}
      onNavigate={(targetRoute, params) => {
        // Signup is not supported - redirect to login
        if (targetRoute === "signup") {
          window.location.href = AUTH_ROUTES.LOGIN;
          return;
        }

        const routeToPath: Record<AuthPageRoute, string> = {
          "login": AUTH_ROUTES.LOGIN,
          "signup": AUTH_ROUTES.LOGIN,
          "forgot-password": AUTH_ROUTES.FORGOT_PASSWORD,
          "otp-verification": AUTH_ROUTES.FORGOT_PASSWORD,
          "validate-forgot-password": AUTH_ROUTES.RESET_PASSWORD,
          "account-linking": AUTH_ROUTES.LOGIN,
        };
        const path = routeToPath[targetRoute] || AUTH_ROUTES.LOGIN;
        const queryString = params ? "?" + new URLSearchParams(params).toString() : "";
        window.location.href = path + queryString;
      }}
      onAuthSuccess={() => {
        window.location.href = DashboardRoutes.INDEX;
      }}
      onBackToHome={() => {
        window.location.href = AUTH_ROUTES.LOGIN;
      }}
      successRedirectPath={DashboardRoutes.INDEX}
    />
  );
}

export const router = createBrowserRouter([
  // Auth routes (login only, no signup)
  {
    path: AUTH_ROUTES.LOGIN,
    element: <AuthRoutePage />,
  },
  {
    path: AUTH_ROUTES.FORGOT_PASSWORD,
    element: <AuthRoutePage />,
  },
  {
    path: AUTH_ROUTES.RESET_PASSWORD,
    element: <AuthRoutePage />,
  },

  // OAuth success callback: the backend redirects here with the token to persist the session.
  {
    path: "/auth/callback",
    element: (
      <LazyPage>
        <OAuthCallbackPage />
      </LazyPage>
    ),
  },

  // Redirect signup attempts to login
  {
    path: AUTH_ROUTES.SIGNUP,
    element: <Navigate to={AUTH_ROUTES.LOGIN} replace />,
  },

  // Protected routes - ALL users must be logged in
  {
    element: <AuthGuard />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AdminLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          // Dashboard
          {
            path: DashboardRoutes.INDEX,
            element: (
              <PrivateRoute permissions={[DashboardPermissions.READ]}>
                <LazyPage><DashboardPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Reservations
          {
            path: ReservationRoutes.LIST,
            element: (
              <PrivateRoute permissions={[ReservationPermissions.READ]}>
                <LazyPage><ReservationListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: ReservationRoutes.DETAIL,
            element: (
              <PrivateRoute permissions={[ReservationPermissions.READ]}>
                <LazyPage><ReservationDetailPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Flights
          {
            path: FlightRoutes.LIST,
            element: (
              <PrivateRoute permissions={[FlightPermissions.READ]}>
                <LazyPage><FlightListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Hotels
          {
            path: HotelRoutes.LIST,
            element: (
              <PrivateRoute permissions={[HotelPermissions.READ]}>
                <LazyPage><HotelListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Packages
          {
            path: PackageRoutes.LIST,
            element: (
              <PrivateRoute permissions={[PackagePermissions.READ]}>
                <LazyPage><PackageListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Transfers
          {
            path: TransferRoutes.LIST,
            element: (
              <PrivateRoute permissions={[TransferPermissions.READ]}>
                <LazyPage><TransferListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Customers
          {
            path: CustomerRoutes.LIST,
            element: (
              <PrivateRoute permissions={[CustomerPermissions.READ]}>
                <LazyPage><CustomerListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: CustomerRoutes.DETAIL,
            element: (
              <PrivateRoute permissions={[CustomerPermissions.READ]}>
                <LazyPage><CustomerDetailPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Reports
          {
            path: ReportRoutes.INDEX,
            element: (
              <PrivateRoute permissions={[ReportPermissions.READ]}>
                <LazyPage><ReportListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Landing Pages
          {
            path: LandingPageRoutes.DASHBOARD,
            element: (
              <PrivateRoute permissions={[LandingPagePermissions.READ]}>
                <LazyPage><LandingPagesDashboardPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: LandingPageRoutes.LIST,
            element: (
              <PrivateRoute permissions={[LandingPagePermissions.READ]}>
                <LazyPage><LandingPageListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: LandingPageRoutes.DETAIL,
            element: (
              <PrivateRoute permissions={[LandingPagePermissions.READ]}>
                <LazyPage><LandingPageDetailPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: LandingPageRoutes.EDITOR,
            element: (
              <PrivateRoute permissions={[LandingPagePermissions.WRITE]}>
                <LazyPage><LandingPageEditorPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: SharedComponentRoutes.LIST,
            element: (
              <PrivateRoute permissions={[LandingPagePermissions.READ]}>
                <LazyPage><SharedComponentListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: SharedComponentRoutes.CREATE,
            element: (
              <PrivateRoute permissions={[LandingPagePermissions.WRITE]}>
                <LazyPage><SharedComponentFormPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: SharedComponentRoutes.EDIT,
            element: (
              <PrivateRoute permissions={[LandingPagePermissions.READ]}>
                <LazyPage><SharedComponentFormPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Settings
          {
            path: SettingsRoutes.INDEX,
            element: (
              <PrivateRoute permissions={[SettingsPermissions.READ]}>
                <LazyPage><SettingsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: SettingsRoutes.USERS,
            element: (
              <PrivateRoute permissions={[SettingsPermissions.USERS]}>
                <LazyPage><UserPermissionsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Providers (inside Settings)
          {
            path: ProviderRoutes.LIST,
            element: (
              <PrivateRoute permissions={[ProviderPermissions.READ]}>
                <LazyPage><ProviderListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: ProviderRoutes.CREATE,
            element: (
              <PrivateRoute permissions={[ProviderPermissions.WRITE]}>
                <LazyPage><ProviderFormPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: ProviderRoutes.DETAIL,
            element: (
              <PrivateRoute permissions={[ProviderPermissions.READ]}>
                <LazyPage><ProviderDetailPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: ProviderRoutes.EDIT,
            element: (
              <PrivateRoute permissions={[ProviderPermissions.WRITE]}>
                <LazyPage><ProviderFormPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Issue Supports (inside Settings)
          {
            path: IssueSupportTemplateRoutes.LIST,
            element: (
              <PrivateRoute permissions={[IssueSupportTemplatePermissions.READ]}>
                <LazyPage><IssueSupportTemplateListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: IssueSupportTemplateRoutes.CREATE,
            element: (
              <PrivateRoute permissions={[IssueSupportTemplatePermissions.WRITE]}>
                <LazyPage><IssueSupportTemplateFormPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: IssueSupportTemplateRoutes.EDIT,
            element: (
              <PrivateRoute permissions={[IssueSupportTemplatePermissions.WRITE]}>
                <LazyPage><IssueSupportTemplateFormPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: IssueSupportTemplateRoutes.DETAIL,
            element: (
              <PrivateRoute permissions={[IssueSupportTemplatePermissions.READ]}>
                <LazyPage><IssueSupportTemplateDetailPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Airlines (inside Settings) - check-in URLs used by travel emails
          {
            path: EmailTemplateRoutes.AIRLINES,
            element: (
              <PrivateRoute permissions={[AirlinePermissions.READ]}>
                <LazyPage><AirlinesPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Email brand theme (inside Settings) - the look travel email blocks and starters use
          {
            path: EmailTemplateRoutes.BRAND,
            element: (
              <PrivateRoute permissions={[EmailTemplatePermissions.READ]}>
                <LazyPage><EmailBrandPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Marketing image library
          {
            path: AssetRoutes.INDEX,
            element: (
              <PrivateRoute permissions={[...ASSET_BROWSE_PERMISSIONS]}>
                <LazyPage><AssetLibraryPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Email templates
          {
            path: EmailTemplateRoutes.LIST,
            element: (
              <PrivateRoute permissions={[EmailTemplatePermissions.READ]}>
                <LazyPage><EmailTemplatesListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: EmailTemplateRoutes.CREATE,
            element: (
              <PrivateRoute permissions={[EmailTemplatePermissions.WRITE]}>
                <LazyPage><EmailTemplateEditorPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: EmailTemplateRoutes.EDIT,
            element: (
              <PrivateRoute permissions={[EmailTemplatePermissions.READ]}>
                <LazyPage><EmailTemplateEditorPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Email automations (trigger + conditions → template)
          {
            path: EmailAutomationRoutes.LIST,
            element: (
              <PrivateRoute permissions={[EmailAutomationPermissions.READ]}>
                <LazyPage><EmailAutomationsListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: EmailAutomationRoutes.CREATE,
            element: (
              <PrivateRoute permissions={[EmailAutomationPermissions.WRITE]}>
                <LazyPage><EmailAutomationEditorPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: EmailAutomationRoutes.EDIT,
            element: (
              <PrivateRoute permissions={[EmailAutomationPermissions.READ]}>
                <LazyPage><EmailAutomationEditorPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: EmailAutomationRoutes.SENDS,
            element: (
              <PrivateRoute permissions={[EmailAutomationPermissions.READ]}>
                <LazyPage><EmailAutomationSendLogPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Upsells (offers for the booking's destination) and their destination overrides
          {
            path: TravelUpsellRoutes.LIST,
            element: (
              <PrivateRoute permissions={[TravelUpsellPermissions.READ]}>
                <LazyPage><TravelUpsellsListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: TravelUpsellRoutes.CREATE,
            element: (
              <PrivateRoute permissions={[TravelUpsellPermissions.WRITE]}>
                <LazyPage><TravelUpsellEditorPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: TravelUpsellRoutes.DESTINATIONS,
            element: (
              <PrivateRoute permissions={[TravelUpsellPermissions.READ]}>
                <LazyPage><TravelUpsellDestinationsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: TravelUpsellRoutes.EDIT,
            element: (
              <PrivateRoute permissions={[TravelUpsellPermissions.READ]}>
                <LazyPage><TravelUpsellEditorPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Support tickets (inside Settings)
          {
            path: SupportRoutes.LIST,
            element: (
              <PrivateRoute permissions={[SupportPermissions.READ]}>
                <LazyPage><SupportTicketListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: SupportRoutes.DETAIL,
            element: (
              <PrivateRoute permissions={[SupportPermissions.READ]}>
                <LazyPage><SupportTicketDetailPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Deals
          {
            path: DealsRoutes.TRAVEL_DEALS_LIST,
            element: (
              <PrivateRoute permissions={[DealsPermissions.DEALS_READ]}>
                <LazyPage><TravelDealsListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: DealsRoutes.TRAVEL_DEALS_CREATE,
            element: (
              <PrivateRoute permissions={[DealsPermissions.DEALS_WRITE]}>
                <LazyPage><TravelDealsFormPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: DealsRoutes.TRAVEL_DEALS_EDIT,
            element: (
              <PrivateRoute permissions={[DealsPermissions.DEALS_WRITE]}>
                <LazyPage><TravelDealsFormPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: DealsRoutes.COMBO_OFFERS_LIST,
            element: (
              <PrivateRoute permissions={[DealsPermissions.DEALS_READ]}>
                <LazyPage><ComboOffersListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: DealsRoutes.COMBO_OFFERS_CREATE,
            element: (
              <PrivateRoute permissions={[DealsPermissions.DEALS_WRITE]}>
                <LazyPage><ComboOffersFormPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: DealsRoutes.COMBO_OFFERS_EDIT,
            element: (
              <PrivateRoute permissions={[DealsPermissions.DEALS_WRITE]}>
                <LazyPage><ComboOffersFormPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Travel Analytics
          {
            path: TravelAnalyticsRoutes.INDEX,
            element: (
              <PrivateRoute permissions={[TravelAnalyticsPermissions.VIEW]}>
                <LazyPage><AnalyticsOverviewPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: TravelAnalyticsRoutes.OVERVIEW,
            element: (
              <PrivateRoute permissions={[TravelAnalyticsPermissions.VIEW]}>
                <LazyPage><AnalyticsOverviewPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: TravelAnalyticsRoutes.FLIGHTS,
            element: (
              <PrivateRoute permissions={[TravelAnalyticsPermissions.VIEW]}>
                <LazyPage><FlightAnalyticsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: TravelAnalyticsRoutes.HOTELS,
            element: (
              <PrivateRoute permissions={[TravelAnalyticsPermissions.VIEW]}>
                <LazyPage><HotelAnalyticsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: TravelAnalyticsRoutes.CARS,
            element: (
              <PrivateRoute permissions={[TravelAnalyticsPermissions.VIEW]}>
                <LazyPage><CarAnalyticsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: TravelAnalyticsRoutes.PERFORMANCE,
            element: (
              <PrivateRoute permissions={[TravelAnalyticsPermissions.VIEW]}>
                <LazyPage><PerformanceAnalyticsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: TravelAnalyticsRoutes.QUALITY,
            element: (
              <PrivateRoute permissions={[TravelAnalyticsPermissions.VIEW]}>
                <LazyPage><SearchQualityPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: TravelAnalyticsRoutes.FUNNEL,
            element: (
              <PrivateRoute permissions={[TravelAnalyticsPermissions.VIEW]}>
                <LazyPage><ConversionFunnelPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: TravelAnalyticsRoutes.PROVIDERS,
            element: (
              <PrivateRoute permissions={[TravelAnalyticsPermissions.PROVIDERS_VIEW]}>
                <LazyPage><ProviderAnalyticsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: TravelAnalyticsRoutes.GEOGRAPHIC,
            element: (
              <PrivateRoute permissions={[TravelAnalyticsPermissions.VIEW]}>
                <LazyPage><GeographicAnalyticsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: TravelAnalyticsRoutes.SEARCHES,
            element: (
              <PrivateRoute permissions={[TravelAnalyticsPermissions.VIEW]}>
                <LazyPage><SearchExplorerPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Advanced Analytics (self-service / Mixpanel-like)
          {
            path: AdvancedAnalyticsRoutes.INDEX,
            element: <Navigate to={AdvancedAnalyticsRoutes.FUNNELS} replace />,
          },
          {
            path: AdvancedAnalyticsRoutes.FUNNELS,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.VIEW]}>
                <LazyPage><FunnelsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: AdvancedAnalyticsRoutes.FLOWS,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.VIEW]}>
                <LazyPage><FlowsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: AdvancedAnalyticsRoutes.RETENTION,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.VIEW]}>
                <LazyPage><RetentionPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: AdvancedAnalyticsRoutes.COHORTS,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.VIEW]}>
                <LazyPage><CohortsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: AdvancedAnalyticsRoutes.COHORT_DETAIL,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.VIEW]}>
                <LazyPage><CohortDetailPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: AdvancedAnalyticsRoutes.USERS,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.VIEW]}>
                <LazyPage><AdvUsersPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: AdvancedAnalyticsRoutes.USER_PROFILE,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.VIEW]}>
                <LazyPage><UserProfilePage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: AdvancedAnalyticsRoutes.BOARDS,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.VIEW]}>
                <LazyPage><BoardsListPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: AdvancedAnalyticsRoutes.BOARD_DETAIL,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.VIEW]}>
                <LazyPage><BoardDetailPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: AdvancedAnalyticsRoutes.REPORTS,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.VIEW]}>
                <LazyPage><SavedReportsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: AdvancedAnalyticsRoutes.ALERTS,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.VIEW]}>
                <LazyPage><AlertsPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: AdvancedAnalyticsRoutes.EVENTS,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.VIEW]}>
                <LazyPage><EventExplorerPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: AdvancedAnalyticsRoutes.DICTIONARY,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.VIEW]}>
                <LazyPage><DataDictionaryPage /></LazyPage>
              </PrivateRoute>
            ),
          },
          {
            path: AdvancedAnalyticsRoutes.GOVERNANCE,
            element: (
              <PrivateRoute permissions={[AdvancedAnalyticsPermissions.GOVERNANCE]}>
                <LazyPage><GovernancePage /></LazyPage>
              </PrivateRoute>
            ),
          },
          // Unauthorized page
          {
            path: "/unauthorized",
            element: <LazyPage><UnauthorizedPage /></LazyPage>,
          },
          // Catch all - redirect to dashboard
          {
            path: "*",
            element: <Navigate to={DashboardRoutes.INDEX} replace />,
          },
        ],
      },
    ],
  },

  // All other routes redirect to login
  {
    path: "*",
    element: <Navigate to={AUTH_ROUTES.LOGIN} replace />,
  },
]);
